import { i18n } from '../i18n.js';
import { showAchievementBanner } from './banner.js';
import { MoonfallCloud } from '../convex-client.js';
import { Firebase } from '../firebase-loader.js';
import { InviteQuest } from './invite-quest.js';
import { ExchangeQuest } from './exchange-quest.js';
import { Referrals } from './referrals.js';
/**
 * UI Quests Module
 * Renders daily quests in the lobby and shows completion banners.
 */


import { LobbyQuestsToggle } from './lobby-quests-toggle.js';
let Quests = {};

Quests = (function () {
  const DEBUG_STORAGE_KEY = 'questDebug';
  const SEEN_STORAGE_KEY = 'questCompletedSeenIds';
  const SEEN_STORAGE_LIMIT = 200;
  // Accounts whose quests this browser has read before (see renderQuests).
  const FOLLOWED_STORAGE_KEY = 'questCompletionFollowedAccounts';
  const FOLLOWED_STORAGE_LIMIT = 20;
  const TICK_CLASS = 'lobby-quest-item--tick';
  const seenCompletedQuestIds = new Set(loadStoredIds(SEEN_STORAGE_KEY));
  const followedAccounts = new Set(loadStoredIds(FOLLOWED_STORAGE_KEY));
  const pageStartedAt = Date.now();
  // Progress (wins) per quest id from the previous render, so an increase
  // can pulse the quest item exactly once.
  const lastQuestWins = new Map();
  // The quest each rolling row was built from, so its words can be rewritten
  // in place when the translations arrive (see `relocalize`).
  const questForRow = new WeakMap();
  let languageListenerBound = false;
  let refreshVersion = 0;
  let assignmentSettlement = null;
  // Remembered from the last render so the invitation slot can re-issue the
  // count when it appears or resolves, without refetching the quests.
  let lastActiveCount = 0;
  let lastSlots = 3;
  // Whether any quest read has been drawn. Until one has, the count is not
  // known and stays blank (see syncSlotCount).
  let questsAnswered = false;
  const elements = {
    container: null,
    list: null,
    empty: null,
    count: null,
    debugControls: null
  };

  function resolveElements() {
    if (!elements.container || !elements.container.isConnected) {
      elements.container = document.getElementById('lobby-quests');
      elements.list = document.getElementById('lobby-quests-list');
      elements.empty = document.getElementById('lobby-quests-empty');
      elements.count = document.getElementById('lobby-quests-count');
      elements.debugControls = null;
    }
  }

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    /* The FALLBACK is interpolated too. It is returned verbatim whenever
       i18n has not resolved — the normal case on a cold load — and the
       ui.json wording these fallbacks mirror carries `{{name}}`
       placeholders, so a user could be shown a literal `{{count}}`. Both
       brace styles are handled because the copy uses both. */
    const source = translated && translated !== key ? translated : fallback;
    return Object.entries(replacements || {}).reduce(
        (text, [name, value]) => String(text)
            .split(`{{${name}}}`).join(String(value))
            .split(`{${name}}`).join(String(value)),
        String(source == null ? key : source),
    );
  }

  function trimText(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function currentLanguage() {
    return i18n?.getLanguage?.() || 'en';
  }

  function resolveTargetWins(quest) {
    const conditionWins = quest?.condition?.targetWins;
    if (Number.isFinite(conditionWins)) return Math.max(1, Math.floor(conditionWins));
    if (Number.isFinite(quest?.targetWins)) return Math.max(1, Math.floor(quest.targetWins));
    return 0;
  }

  function buildEnglishWinWithCardFallback(targetWins, cardName) {
    const label = targetWins === 1 ? 'game' : 'games';
    return `Win ${targetWins} ${label} with ${cardName} in your deck.`;
  }

  function buildQuestText(quest) {
    const targetWins = resolveTargetWins(quest);
    const cardName = trimText(quest?.cardName);
    if (targetWins > 0 && cardName && quest?.condition?.type === 'win_with_card') {
      const fallback = buildEnglishWinWithCardFallback(targetWins, cardName);
      if (targetWins === 1) {
        return uiText('quests.winWithCardOne', fallback, { targetWins, cardName });
      }
      return uiText('quests.winWithCardMany', fallback, { targetWins, cardName });
    }

    const storedText = trimText(quest?.text);
    if (storedText && !i18n?.t) return storedText;
    return uiText('quests.genericFallback', storedText || 'Win games with a card in your deck.');
  }

  /* Opt-in per visit (`?questsDebug=1`) or pinned (`localStorage.questDebug`).
     The server's DEBUG_MODE only AUTHORIZES the debug mutations; it used to
     also show this panel, and since every local `.env` carries
     DEBUG_MODE=true that put six developer buttons under the quest list of
     every local lobby, all the time. Guarded by tests/e2e/lobbyQuests.e2e.test.js. */
  function isDebugEnabled() {
    try {
      const params = new URLSearchParams(window.location.search || '');
      if (params.get('questsDebug') === '1') return true;
      if (window.localStorage?.getItem(DEBUG_STORAGE_KEY) === 'true') return true;
    } catch (_) {
      return false;
    }
    return false;
  }

  function setDebugStatus(message, tone = 'info') {
    const status = elements.debugControls?.querySelector('.lobby-quests-debug-status');
    if (!status) return;
    status.textContent = message || '';
    status.dataset.tone = message ? tone : '';
  }

  // Run a debug mutation, then surface a TRUTHFUL outcome (or the real error)
  // in the status line — these controls must never fail silently the way the
  // old counter-bump did. Diagnostic strings are English-only on purpose: these
  // controls are developer-facing (DEBUG_MODE) and never shipped to players.
  async function runDebugAction(button, resultToMessage) {
    if (!button) return;
    button.disabled = true;
    setDebugStatus('Working…', 'info');
    try {
      const result = await resultToMessage.run();
      await refresh();
      /* Some actions advance a PINNED row rather than the rotation, and
         `refresh()` re-reads those on its own — but an explicit re-read lets
         the action flash the row it just moved. */
      if (typeof resultToMessage.after === 'function') {
        try { await resultToMessage.after(result); } catch (_) { /* decoration */ }
      }
      setDebugStatus(resultToMessage.describe(result), 'info');
    } catch (err) {
      const raw = String(err?.message || err || '');
      const disabled = /quest debug mode is disabled/i.test(raw);
      setDebugStatus(
        disabled
          ? 'Server debug mode is off (set DEBUG_MODE=true and restart).'
          : `Debug action failed: ${raw}`,
        'error',
      );
    } finally {
      button.disabled = false;
    }
  }

  function ensureDebugControls() {
    if (!elements.container || !isDebugEnabled()) return;
    if (elements.debugControls) {
      updateDebugControlsText();
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'lobby-quests-debug';

    const buttons = document.createElement('div');
    buttons.className = 'lobby-quests-debug-row';

    const addQuest = document.createElement('button');
    addQuest.type = 'button';
    addQuest.className = 'lobby-quests-debug-btn';
    addQuest.dataset.questDebugAction = 'add';
    addQuest.addEventListener('click', () => runDebugAction(addQuest, {
      run: () => MoonfallCloud?.quests?.debugAssignQuest?.(1),
      describe: (r) => (r?.assignedCount > 0
        ? 'Assigned a new quest.'
        : `No free slot (${r?.activeCount ?? '?'}/${r?.slots ?? '?'}) — complete one first.`),
    }));

    // Honest win: routes through the real progress rule against your real decks.
    const simulateWin = document.createElement('button');
    simulateWin.type = 'button';
    simulateWin.className = 'lobby-quests-debug-btn';
    simulateWin.dataset.questDebugAction = 'simulate';
    simulateWin.addEventListener('click', () => runDebugAction(simulateWin, {
      run: () => MoonfallCloud?.quests?.debugSimulateWin?.(),
      describe: (r) => {
        if (!r) return 'Debug action failed: no result.';
        /* A simulated win banks a Moon Exchange win too, and it does so whether
           or not any quest was there to advance. Saying only "no active quests"
           would report nothing happened while a counter moved — the same
           half-truth that made this button look broken in the first place. */
        const exchange = r.exchangeUnlocked
          ? ' Moon Exchange UNLOCKED.'
          : (Number.isFinite(r.exchangeFinished) ? ` Moon Exchange: ${r.exchangeFinished}.` : '');
        if ((r.questCount ?? 0) === 0) {
          return `No active quests to advance — add one first.${exchange}`;
        }
        return `Simulated a win with a quest-ready deck: advanced ${r.advancedCount ?? 0}/${r.questCount} quest(s) `
          + `(+1 each, real rule)${r.completedCount ? `, ${r.completedCount} completed` : ''}.${exchange}`;
      },
      after: () => { void ExchangeQuest?.refresh?.({ flash: true }); },
    }));

    // Artificial: bypasses the real rule, only to see the completion banner.
    const forceComplete = document.createElement('button');
    forceComplete.type = 'button';
    forceComplete.className = 'lobby-quests-debug-btn lobby-quests-debug-btn--artificial';
    forceComplete.dataset.questDebugAction = 'complete';
    forceComplete.addEventListener('click', () => runDebugAction(forceComplete, {
      run: () => MoonfallCloud?.quests?.debugForceComplete?.(),
      describe: (r) => `Force-completed ${r?.completedCount ?? 0} quest(s) — artificial, not how wins work.`,
    }));

    /* THE TWO MILESTONES THE RAIL PINS, on demand.
       Ten wins to open the Moon Exchange and three finished matches to settle a
       referral are the right costs for a player and absurd ones for checking
       that the rail, the ceremony and the payout look right. Both walk the same
       recorders a real match does — the point is to watch the real thing land,
       not to fake its result — so the counters, the banner, the packs and the
       Moonlight all arrive through their normal paths. */
    const exchangeWin = document.createElement('button');
    exchangeWin.type = 'button';
    exchangeWin.className = 'lobby-quests-debug-btn';
    exchangeWin.dataset.questDebugAction = 'exchange-win';
    exchangeWin.addEventListener('click', () => runDebugAction(exchangeWin, {
      run: () => MoonfallCloud?.quests?.debugRecordExchangeWin?.(),
      describe: (r) => {
        if (!r) return 'Debug action failed: no result.';
        if (r.unlocked) return 'Moon Exchange unlocked — the rail row should be gone.';
        return `Banked a win: ${r.finished ?? '?'}/${r.required ?? '?'} toward the Moon Exchange.`;
      },
      after: () => { void ExchangeQuest?.refresh?.({ flash: true }); },
    }));

    const referralFinish = document.createElement('button');
    referralFinish.type = 'button';
    referralFinish.className = 'lobby-quests-debug-btn';
    referralFinish.dataset.questDebugAction = 'referral-finish';
    referralFinish.addEventListener('click', () => runDebugAction(referralFinish, {
      run: () => MoonfallCloud?.quests?.debugRecordReferralFinish?.(),
      /* EVERY STATUS, NAMED. The recorder reports `status`, not `reason`, and
         it has four of them plus a `duplicate` no-op — so the original single
         fall-through printed "Banked a finish: ?/?" for an un-invited account,
         an expired referral and a match that was NOT counted. Claiming progress
         that did not happen is the one thing a debug control must never do. */
      describe: (r) => {
        if (!r) return 'Debug action failed: no result.';
        if (r.rewarded) return 'Referral settled: 3 card packs and Moonlight to both sides.';
        const progress = `${r.completedMatches ?? '?'}/${r.qualifyingMatches ?? '?'}`;
        switch (r.status) {
          case 'not_referred':
            return 'This account was not invited by anyone — nothing to advance.';
          case 'expired':
            return 'That referral has expired — nothing was advanced.';
          case 'qualified':
            return `Already settled (${progress}) — nothing left to advance.`;
          case 'pending':
            return r.duplicate
              ? `Already counted — still ${progress} toward the referral.`
              : `Banked a finish: ${progress} toward the referral.`;
          default:
            return `Referral unchanged (status: ${r.status ?? 'unknown'}).`;
        }
      },
      /* SHOW THE CEREMONY THIS BUTTON JUST EARNED. `consumeLobbyRewards` is
         otherwise only reached on boot, on lobby entry and on returning from a
         match — so pressing this granted 3 packs and $5 of Moonlight and then
         showed nothing at all until the next reload. Reported exactly that way:
         "nothing happens on the 3 packs win. seems like it was technically
         given. but no visual announcement."
         It is the real consumer, not a debug-only imitation, so what appears is
         the ceremony a real payout produces. */
      after: async () => {
        InviteQuest?.refresh?.({ flash: true });
        try { await Referrals?.consumeLobbyRewards?.(); } catch (_) { /* reward is durable */ }
      },
    }));

    /* PUT THE INVITE QUEST BACK. Once a referral settles its row leaves the
       rail permanently, and the owner's only other route to seeing it again was
       deleting and recreating his account. This resets one row instead. */
    const referralReset = document.createElement('button');
    referralReset.type = 'button';
    referralReset.className = 'lobby-quests-debug-btn lobby-quests-debug-btn--artificial';
    referralReset.dataset.questDebugAction = 'referral-reset';
    referralReset.addEventListener('click', () => runDebugAction(referralReset, {
      run: () => MoonfallCloud?.quests?.debugCreatePendingReferral?.(),
      describe: (r) => {
        if (!r) return 'Debug action failed: no result.';
        if (r.created) {
          return `Pending referral ${r.reset ? 're-armed' : 'created'} — `
            + `0/${r.qualifyingMatches ?? '?'} finishes toward 3 packs.`;
        }
        if (r.reason === 'no_other_account_to_invite_from') {
          return 'No other account exists on this deployment to invite from.';
        }
        return `Could not create a pending referral (${r.reason || 'unknown'}).`;
      },
      after: () => { void InviteQuest?.refresh?.({ flash: true }); },
    }));

    buttons.appendChild(addQuest);
    buttons.appendChild(simulateWin);
    buttons.appendChild(forceComplete);
    buttons.appendChild(exchangeWin);
    buttons.appendChild(referralFinish);
    buttons.appendChild(referralReset);

    const status = document.createElement('p');
    status.className = 'lobby-quests-debug-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    wrapper.appendChild(buttons);
    wrapper.appendChild(status);
    elements.container.appendChild(wrapper);
    elements.debugControls = wrapper;
    updateDebugControlsText();
  }

  function updateDebugControlsText() {
    const controls = elements.debugControls;
    if (!controls) return;
    const labels = {
      add: uiText('quests.debugAdd', 'Debug +1 Quest'),
      simulate: uiText('quests.debugSimulate', 'Simulate Win'),
      complete: uiText('quests.debugForceComplete', 'Force complete (artificial)'),
      /* Quoted, because a hyphen cannot be a bare key — and the quoting is
         exactly why these two were forgotten when the buttons were added.
         A button missing from this map renders BLANK: the creation code never
         sets textContent, so it becomes an invisible sliver that still passes
         every click a test makes. Guarded by
         tests/unit/ui/questDebugButtonLabels.test.js. */
      'exchange-win': uiText('quests.debugExchangeWin', 'Moon Exchange +1 win'),
      'referral-finish': uiText('quests.debugReferralFinish', 'Referral +1 finish (3 packs)'),
      'referral-reset': uiText('quests.debugReferralReset', 'Referral: re-arm pending quest'),
    };
    controls.querySelectorAll('[data-quest-debug-action]').forEach((button) => {
      const action = button.dataset.questDebugAction;
      if (labels[action]) setText(button, labels[action]);
    });
  }

  function buildQuestItem(quest) {
    const item = document.createElement('div');
    item.className = 'lobby-quest-item';

    const text = document.createElement('div');
    text.className = 'lobby-quest-text';
    text.textContent = buildQuestText(quest);
    questForRow.set(item, quest);

    const progress = document.createElement('div');
    progress.className = 'lobby-quest-progress';
    const wins = Number.isFinite(quest?.progress?.wins) ? quest.progress.wins : 0;
    const targetWins = resolveTargetWins(quest);
    progress.textContent = targetWins > 0 ? `${wins}/${targetWins}` : `${wins}`;

    item.appendChild(text);
    item.appendChild(progress);
    // A row whose bar is full wears the check (hub.css) until the server
    // retires it.
    if (targetWins > 0 && wins >= targetWins) item.classList.add('lobby-quest-item--done');

    // Progress bar (the retention loop deserves more than a bare number).
    if (targetWins > 0) {
      const bar = document.createElement('div');
      bar.className = 'lobby-quest-bar';
      const fill = document.createElement('div');
      fill.className = 'lobby-quest-bar-fill';
      fill.style.width = `${Math.max(0, Math.min(100, (wins / targetWins) * 100))}%`;
      bar.appendChild(fill);
      item.appendChild(bar);
    }

    // One-shot tick pulse when progress advanced since the previous render.
    if (quest?.id) {
      const prevWins = lastQuestWins.get(quest.id);
      if (Number.isFinite(prevWins) && wins > prevWins) {
        armTick(item);
      }
      lastQuestWins.set(quest.id, wins);
    }
    return item;
  }

  /**
   * THE PROGRESS PULSE PLAYS ONCE.
   *
   * The tick class carries a 600 ms brightness pulse and a 700 ms shine along
   * the bar (lobby.css). It used to stay on the row, and a CSS animation
   * restarts whenever its element comes back from `display: none` — which the
   * rail does after a match, after the Foundry and on every open of the
   * phone's Quests sheet — so the same progress was celebrated again and
   * again (flicker hunt, 25 September 2026). The class is spent when its LAST
   * animation ends or is cut short while playing: never on a timer, which
   * would spend a pulse a closed sheet never showed. A spent row is an
   * unchanged row, so the next identical read keeps its node.
   * Shared with the pinned rows (invite-quest.js, exchange-quest.js).
   */
  function armTick(item) {
    if (!item) return;
    item.classList.add(TICK_CLASS);
    const last = item.querySelector('.lobby-quest-bar-fill') ? 'lobby-quest-shine' : 'lobby-quest-tick';
    const spend = (event) => {
      if (event.animationName !== last) return;
      item.classList.remove(TICK_CLASS);
      item.removeEventListener('animationend', spend);
      item.removeEventListener('animationcancel', spend);
    };
    item.addEventListener('animationend', spend);
    item.addEventListener('animationcancel', spend);
  }

  function openSlotText() {
    return uiText('lobby.questSlotOpen', 'Open slot — a new quest arrives daily');
  }

  function buildOpenSlot() {
    const slot = document.createElement('div');
    slot.className = 'lobby-quest-slot';
    const text = document.createElement('div');
    text.className = 'lobby-quest-slot-text';
    text.textContent = openSlotText();
    slot.appendChild(text);
    return slot;
  }

  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  /**
   * THE WORDS AGAIN, ONCE THE TRANSLATIONS ARE IN.
   *
   * `i18n.init()` is not awaited at boot, and on a cold load the quest reads
   * usually beat ui.json (~220 KB, revalidated on every visit) — so the rail is
   * first written from its English fallbacks. Nothing wrote it again until the
   * next refresh: a Japanese reader kept English quests, and the Moon Exchange
   * row, whose helper did not guard against the key, kept its raw keys.
   *
   * Only the words change, in place and only where they differ: the rows are
   * not rebuilt under the player, nothing is re-read (a language is not new
   * data), and an English reader's rail is not touched at all.
   */
  function relocalize() {
    resolveElements();
    const list = elements.list;
    if (!list) return;
    list.querySelectorAll('.lobby-quest-item').forEach((item) => {
      const quest = questForRow.get(item);
      if (quest) setText(item.querySelector('.lobby-quest-text'), buildQuestText(quest));
    });
    const slotText = openSlotText();
    list.querySelectorAll('.lobby-quest-slot-text').forEach((el) => setText(el, slotText));
    InviteQuest?.relocalize?.(list);
    ExchangeQuest?.relocalize?.(list);
    updateDebugControlsText();
  }

  /** One banner for everything a read completed: one quest by name, several counted together. */
  function showCompletionBanner(quests) {
    if (typeof showAchievementBanner !== 'function') return false;
    if (quests.length === 1) {
      const questText = buildQuestText(quests[0]);
      const subtitle = uiText('quests.completeSubtitle', `Card pack awarded. ${questText}`.trim(), { questText });
      showAchievementBanner(uiText('quests.completeTitle', 'Quest Complete!'), subtitle);
      return true;
    }
    const count = quests.length;
    showAchievementBanner(
      uiText('quests.completeManyTitle', '{{count}} Quests Complete!', { count }),
      uiText('quests.completeManySubtitle', '{{count}} card packs awarded.', { count }),
    );
    return true;
  }

  function loadStoredIds(key) {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((id) => typeof id === 'string' && id.length > 0);
    } catch (_) {
      return [];
    }
  }

  function persistStoredIds(key, idSet, limit) {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      const ids = Array.from(idSet);
      const trimmed = ids.length > limit
        ? ids.slice(ids.length - limit)
        : ids;
      window.localStorage.setItem(key, JSON.stringify(trimmed));
    } catch (_) {
      return;
    }
  }

  function currentAccountId() {
    try { return String(Firebase?.getUser?.()?.uid || ''); } catch (_) { return ''; }
  }

  function renderQuests(quests, slots) {
    if (!elements.container || !elements.list || !elements.empty) return;
    const questList = Array.isArray(quests) ? quests : [];

    /* ONE ARRIVAL FOR EVERYTHING A READ COMPLETED. One win can complete
       several quests, and a browser without the stored "seen" set (a new
       device, a cleared or private browser) is told about every completion of
       the last 14 days. All of them used to be shown in the same task: each
       banner handed the previous one off with an exit that starts from full
       opacity, so every banner but the last painted whole for a frame and flew
       out under the next, each with its own chime. Staggered 4500 ms apart
       instead, they became a parade: a veteran's cold boot replayed a banner
       every 4.5 s for most of a minute (eleven in one run), still landing in
       the Collection and in the next match's mulligan (flicker hunt, 25
       September 2026). So one completion keeps its banner by name, and
       several share ONE banner that counts them: none is hidden, and nothing
       waits on a timer to land on another screen. All are marked seen
       together, so a re-read announces nothing twice.

       A BROWSER ANNOUNCES WHAT HAPPENED WHILE IT WAS FOLLOWING THE ACCOUNT.
       Its first read of an account (a new device, a cleared or private
       browser, another account on a shared one) used to announce the whole
       fortnight, already told on the device that played it ("a new device
       announces old completions", 28 September 2026). That read now records
       what is already complete as seen, and announces only completions from
       this page's own session; after it, every completion this browser has
       not shown is news, so a win a reload interrupted is still told. */
    const completed = questList.filter((quest) => quest.status === 'completed');
    const fresh = completed.filter((quest) => quest?.id && !seenCompletedQuestIds.has(quest.id));
    const account = currentAccountId();
    const firstRead = !followedAccounts.has(account);
    const news = firstRead
      ? fresh.filter((quest) => Number(quest.completedAt) >= pageStartedAt)
      : fresh;
    const history = fresh.filter((quest) => !news.includes(quest));
    history.forEach((quest) => seenCompletedQuestIds.add(quest.id));
    const announced = news.length > 0 && showCompletionBanner(news);
    if (announced) news.forEach((quest) => seenCompletedQuestIds.add(quest.id));
    if (announced || history.length) {
      persistStoredIds(SEEN_STORAGE_KEY, seenCompletedQuestIds, SEEN_STORAGE_LIMIT);
    }
    if (firstRead) {
      followedAccounts.add(account);
      persistStoredIds(FOLLOWED_STORAGE_KEY, followedAccounts, FOLLOWED_STORAGE_LIMIT);
    }

    const active = questList.filter((quest) => quest.status === 'active');
    active.sort((a, b) => (a.expiresAt || 0) - (b.expiresAt || 0));

    /* BUILT OFF-SCREEN, SWAPPED IN ONCE — AND NOT AT ALL WHEN NOTHING CHANGED.
       The rail used to be emptied and refilled in place on every render, and
       its pinned rows arrived on their own reads: measured on a 110 ms-RTT
       cold boot, the rows popped in 340 ms after the lobby appeared and the
       panel grew 74 px. The whole list is now composed in a fragment and
       committed with one `replaceChildren`; a render that would produce the
       rows already on screen keeps the very nodes the player is reading
       (docs/asset-loading.md, "reconcile, never rebuild"). */
    const next = document.createDocumentFragment();
    active.forEach((quest) => {
      next.appendChild(buildQuestItem(quest));
    });

    /* The invitation is pinned ABOVE the rolling quests and holds a slot of its
       own, and it must be in place before the count below is written. */
    InviteQuest?.mount?.(next);
    /* The Exchange unlock sits under the invitation, for the same reason and
       on the same terms: pinned, counted in both halves of the fraction, gone
       the moment it is earned. */
    ExchangeQuest?.mount?.(next);

    lastActiveCount = active.length;
    lastSlots = Number.isFinite(slots) ? slots : 3;
    questsAnswered = true;
    /* Every open slot is drawn as an empty row: the panel keeps the same
       silhouette whether it holds zero quests or three, and the row says
       when the next one comes. Not a `.lobby-quest-item` — those are quests. */
    for (let index = 0; index < Math.max(0, lastSlots - active.length); index += 1) {
      next.appendChild(buildOpenSlot());
    }
    if (!sameRows(elements.list, next)) elements.list.replaceChildren(next);
    syncSlotCount();

    ensureDebugControls();
  }

  /** Does the fragment hold exactly the rows the list already shows? */
  function sameRows(list, fragment) {
    const current = list.children;
    const incoming = fragment.children;
    if (current.length !== incoming.length) return false;
    for (let index = 0; index < current.length; index += 1) {
      if (current[index].outerHTML !== incoming[index].outerHTML) return false;
    }
    return true;
  }

  /** The pinned rows alone, for a render that has no quest answer to draw. */
  function mountPinsInPlace() {
    resolveElements();
    if (!elements.list) return;
    InviteQuest?.mount?.(elements.list);
    ExchangeQuest?.mount?.(elements.list);
    syncSlotCount();
  }

  /**
   * THE BOOT VEIL WAITS FOR THIS (main.js, ui/lobby-arrival.js).
   * Settles after the rail's first complete render — or its first failure, so
   * a read that never answers can hold nothing but its own capped veil hold.
   */
  let settleFirstRender = null;
  const firstRender = new Promise((resolve) => {
    settleFirstRender = () => {
      if (!settleFirstRender) return;
      settleFirstRender = null;
      resolve();
    };
  });
  const markRendered = () => { if (settleFirstRender) settleFirstRender(); };
  function whenReady() {
    return firstRender;
  }

  /**
   * Write the rail counter, counting the pinned invitation as a real slot in
   * BOTH halves of the fraction — it is an extra slot the player has, so
   * folding it into the numerator alone would render "4/3".
   */
  /* NO COUNT BEFORE A QUEST READ HAS ANSWERED, AND NO WRITE THAT CHANGES
     NOTHING (17 October 2026). The markup used to ship "0/3" here and on the
     phone's Quests pill, and a failed first read wrote "0/3" too: right after
     sign-in a player with two quests and a pinned row read "0/3", then "3/4".
     A count no read has produced stays blank — its width is reserved in CSS
     (`:empty`), so the real one lands without moving anything. And a re-read
     that brings the same count leaves the text node alone: a poll tick that
     changes nothing must not repaint. */
  function syncSlotCount() {
    resolveElements();
    const pinned = (InviteQuest?.count?.() || 0) + (ExchangeQuest?.count?.() || 0);
    if (elements.count && questsAnswered) {
      setText(elements.count, `${lastActiveCount + pinned}/${lastSlots + pinned}`);
      // The phone control shows this same count; mirroring it here (rather
      // than recomputing it there) is what keeps them from disagreeing.
      LobbyQuestsToggle?.syncCount?.();
    }
    if (elements.empty) {
      /* The open-slot rows already say quests arrive daily; the message is
         for a panel with no rows of any kind. */
      const openSlots = elements.list ? elements.list.querySelectorAll('.lobby-quest-slot').length : 0;
      const display = (lastActiveCount + pinned + openSlots) === 0 ? 'block' : 'none';
      if (elements.empty.style.display !== display) elements.empty.style.display = display;
    }
  }

  async function refresh({ assign = true } = {}) {
    resolveElements();
    if (!elements.container) {
      markRendered();
      return null;
    }
    /* Read the invitation and the Exchange gate alongside the quests: they are
       rendered into the same list, and it is drawn ONCE, when all three have
       answered — a row that lands on its own is a row that pops in. (The
       invitation must not depend on the brief having been dismissed this
       session either: a returning player never sees that screen again.) */
    const pins = Promise.allSettled([
      InviteQuest?.refresh?.({ deferMount: true }),
      ExchangeQuest?.refresh?.({ deferMount: true }),
    ]);
    const load = MoonfallCloud?.isReady?.()
      ? (assign ? MoonfallCloud?.quests?.sync : MoonfallCloud?.quests?.listActive)
      : null;
    if (typeof load !== 'function') {
      await pins;
      mountPinsInPlace();
      markRendered();
      return null;
    }

    const version = ++refreshVersion;
    if (!assign && assignmentSettlement) {
      await assignmentSettlement;
      if (version !== refreshVersion) return null;
    }
    const language = currentLanguage();

    try {
      const request = Promise.resolve(load.call(MoonfallCloud.quests));
      if (assign) {
        const settlement = request.then(() => undefined, () => undefined);
        assignmentSettlement = settlement;
        void settlement.then(() => {
          if (assignmentSettlement === settlement) assignmentSettlement = null;
        });
      }
      const [response] = await Promise.all([request, pins]);
      if (version !== refreshVersion || language !== currentLanguage()) return null;

      const result = Array.isArray(response)
        ? { quests: response, slots: 3 }
        : response;
      renderQuests(result?.quests, result?.slots);
      // Written only when it changes: an unchanged re-read touches nothing.
      if (elements.container.dataset.renderedLanguage !== language) {
        elements.container.dataset.renderedLanguage = language;
      }
      window.dispatchEvent(new CustomEvent('moonfall:questsrendered', {
        detail: { language },
      }));
      markRendered();
      return result;
    } catch (err) {
      if (assign || version === refreshVersion) {
        console.error('[Quests] Failed to refresh quests:', err);
      }
      /* A failed quest read is not an empty rail: keep what is on screen and
         still place the pinned rows that did answer. */
      await pins;
      if (version === refreshVersion) mountPinsInPlace();
      markRendered();
      return null;
    }
  }

  /**
   * THE RAIL CHANGES LANGUAGE WITH EVERYTHING ELSE.
   *
   * Its card names come translated from the server, so a switch used to
   * rewrite every other label and then, ~0.55 s later, rebuild this rail in
   * the new language (measured 25 September 2026). The switch now asks first
   * (i18n `registerLanguagePreparer`, capped) and applies this answer in the
   * same task as its own rewrite; the announcement that follows finds the rail
   * already in that language and reads nothing.
   *
   * A rail nobody can see — folded behind the phone's Quests pill, or under a
   * match — has nothing to change in step with, so it never holds the switch
   * (on the phone seat it held the whole lobby 440 ms for a read of a hidden
   * list); the announcement re-reads it out of sight.
   */
  function prepareLanguage(lang) {
    resolveElements();
    if (!elements.container || !elements.container.isConnected) return null;
    if (typeof elements.container.checkVisibility === 'function' && !elements.container.checkVisibility()) return null;
    const read = MoonfallCloud?.isReady?.() ? MoonfallCloud?.quests?.listActive : null;
    if (typeof read !== 'function') return null;
    return Promise.resolve(read.call(MoonfallCloud.quests, { language: lang })).then((response) => () => {
      if (!elements.container?.isConnected) return;
      refreshVersion += 1; // an older read in flight must not overwrite this one
      const result = Array.isArray(response) ? { quests: response, slots: 3 } : response;
      renderQuests(result?.quests, result?.slots);
      relocalize();
      elements.container.dataset.renderedLanguage = lang;
    });
  }

  function bindLanguageListener() {
    if (languageListenerBound) return;
    i18n?.registerLanguagePreparer?.(prepareLanguage);
    window.addEventListener('moonfall:languagechange', (event) => {
      resolveElements();
      if (!elements.container || !elements.container.isConnected) return;
      // Already applied by the switch itself (prepareLanguage).
      const language = /** @type {CustomEvent} */ (event).detail?.language;
      if (language && elements.container.dataset.renderedLanguage === language) return;
      relocalize();
      void refresh({ assign: false });
    });
    window.addEventListener('moonfall:i18nready', relocalize);
    languageListenerBound = true;
  }

  function init() {
    resolveElements();
    bindLanguageListener();
    if (elements.container) {
      elements.container.classList.add('is-visible');
    }
    LobbyQuestsToggle?.init?.();
    ensureDebugControls();
    void refresh();
  }

  return {
    init,
    refresh,
    syncSlotCount,
    whenReady,
    armTick
  };
})();

export { Quests };
